'use client';

import { useEffect, useState } from 'react';
import { eventsApi } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';

const POLL_MS = 15000;

export function useEventChatUnread(eventId: string | null | undefined): number {
  const [count, setCount] = useState(0);

  const authChecking = useAuthStore((state) => state.authChecking);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const enabled = Boolean(eventId) && !authChecking && isAuthenticated;

  useEffect(() => {
    if (!enabled || !eventId) {
      return;
    }

    let cancelled = false;
    let interval: NodeJS.Timeout | null = null;

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const load = async () => {
      if (!useAuthStore.getState().isAuthenticated) {
        stopPolling();
        return false;
      }
      try {
        const res = await eventsApi.getChatUnread(eventId);
        if (!cancelled) setCount(res.data?.data?.unread_count ?? 0);
        return true;
      } catch (err: unknown) {
        if (!cancelled) setCount(0);
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          stopPolling();
          return false;
        }
        return true;
      }
    };

    const handleSessionExpired = () => {
      stopPolling();
      if (!cancelled) setCount(0);
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    window.addEventListener('auth:unauthenticated', handleSessionExpired);

    void load().then((keepPolling) => {
      if (cancelled || keepPolling === false) return;
      interval = setInterval(() => {
        void load();
      }, POLL_MS);
    });
    return () => {
      cancelled = true;
      stopPolling();
      window.removeEventListener('auth:session-expired', handleSessionExpired);
      window.removeEventListener('auth:unauthenticated', handleSessionExpired);
    };
  }, [eventId, enabled]);

  return enabled ? count : 0;
}
